import React, { useCallback, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "components/context/UserContext";
import type { SupademoDemoDto } from "services/supademosService";
import { upsertUserSupademoEngagement } from "services/supademosService";
import "./supademoBtns.css";
declare const Supademo: JObj;

const normalizePath = (p: string) => (p || "").split("?")[0].split("#")[0];

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const patternToRegex = (pattern: string) => {
  const trimmed = (pattern || "").trim();
  if (!trimmed) return null;

  // Support "*" wildcard (optional, but handy)
  const reStr = `^${trimmed.split("*").map(escapeRegExp).join(".*")}$`;
  return new RegExp(reStr);
};

const pathMatches = (pattern: string, pathname: string) => {
  const pat = (pattern || "").trim();
  const path = normalizePath(pathname);

  if (!pat) return false;

  // Wildcard patterns like "/rep/appointments/*"
  if (pat.includes("*")) {
    const re = patternToRegex(pat);
    return !!re?.test(path);
  }

  // Exact match
  if (path === pat) return true;

  // Prefix match on segment boundary (so "/rep/appointments" matches "/rep/appointments/123")
  const withSlash = pat.endsWith("/") ? pat : `${pat}/`;
  if (path.startsWith(withSlash)) return true;

  // Fallback to simple prefix (your "URL contains" requirement, but safer than full includes)
  return path.startsWith(pat);
};

const pickBestDemoForPath = (demos: SupademoDemoDto[], pathname: string) => {
  const matches = (demos || [])
    .filter((d) => !!d?.SupademoKey && !!d?.IsActive)
    .filter((d) => pathMatches(d.PathPattern, pathname))
    // Prefer the most specific path pattern (longest)
    .sort(
      (a, b) => (b.PathPattern?.length || 0) - (a.PathPattern?.length || 0),
    );

  return matches[0] || null;
};

interface SupademoFabProps {
  label?: string;
}
const SupademoFab: React.FC<SupademoFabProps> = ({
  label = "See How To Use MyRep",
}) => {
  const { state } = useContext(UserContext)!;
  const location = useLocation();

  const demo = useMemo(
    () => pickBestDemoForPath(state.supademos || [], location.pathname),
    [state.supademos, location.pathname],
  );

  const hasOpenedBefore = useMemo(() => {
    if (!demo) return false;
    return (
      demo.HasOpenedBefore === 1 ||
      (typeof demo.OpenCount === "number" && demo.OpenCount > 0) ||
      !!demo.LastOpenedAt
    );
  }, [demo]);

  const isOnCalendarPage = useMemo(() => {
    return pathMatches("/client/calendar", location.pathname);
  }, [location.pathname]);

  const onClick = useCallback(() => {
    if (!demo?.SupademoKey) return;

    // Best-effort engagement logging (don't block opening the widget)
    if (typeof demo?.Id === "number" && demo.Id > 0) {
      void upsertUserSupademoEngagement(demo.Id).catch((err) => {
        console.warn("Failed to log Supademo engagement:", err);
      });
    }

    const open = Supademo?.widget?.open;
    if (typeof open !== "function") {
      console.warn("Supademo widget is not available on window yet.");
      return;
    }

    open(String(demo.SupademoKey));
  }, [demo?.SupademoKey, demo?.Id]);

  if (!demo) return null;

  return (
    <div
      className={[
        "supademo-header-btn-container",
        hasOpenedBefore ? "supademo-header-btn-container--end" : "",
      ].join(" ")}
    >
      <button
        className={[
          "supademo-header-btn",
          hasOpenedBefore
            ? "supademo-header-btn--seen"
            : "supademo-header-btn--new",
        ].join(" ")}
        onClick={onClick}
        style={{ marginBottom: isOnCalendarPage ? 16 : 0 }}
      >
        {demo?.Title || label}
      </button>
    </div>
  );
};

export default SupademoFab;
